import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { UserAccessRequestsService } from '@fi-app/user/user-access-requests.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiInfiniteScrollDataSource } from '@fi-shared/fi-items-list/fi-infinite-scroll-data-source';
import { takeUntil } from 'rxjs/operators';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { ObjectTypes } from '@fi-shared/types';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { ILightWeightObject } from '@fi-shared/models';
import { FiDisplayType } from '@fi-shared/types/display-type';
import { isDefined } from '@fi-shared/utils';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { AppTextService } from '@fi-shared/app-text';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { IUserListItemModel } from '@fi-app/user/user';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';

@Component({
    selector: 'fi-user-requests-list',
    templateUrl: './fi-user-requests-list.component.html',
    styleUrls: ['./fi-user-requests-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-user-requests-list' },
    standalone: false,
})
export class FiUserRequestsListComponent
    extends FiInfiniteScrollDataSource<IUserListItemModel>
    implements OnInit, OnDestroy
{
    @Input() isMenuActionsShown: boolean = true;
    @Input() currentListDisplayType: FiDisplayType;

    private userAccessRequestsService = inject(UserAccessRequestsService);
    private detectDevicesService = inject(DetectDevicesService);
    private fiItemsDeleteHelperService = inject(FiItemsDeleteHelperService);
    private appTextService = inject(AppTextService);
    private fiStateService = inject(FiStateService);

    public defaultSortColumnName: string = 'Name';
    public itemsListType = FiItemsListType.UserAccessRequest;
    public objectType = ObjectTypes.UserAccessRequest;

    protected readonly FiStateNames = FiStateNames;
    protected readonly itemsListActions: IItemsListAction2[] = [
        {
            title: this.appTextService.get('accept'),
            onClick: () => this.acceptSelectedUsersRequests(),
        },
        {
            title: this.appTextService.get('common.reject'),
            onClick: () => this.confirmRejection(),
        },
    ];

    constructor(injector: Injector) {
        super(injector, {});
    }

    public ngOnInit(): void {
        this.subscribeOnListDisplayTypeChange();
        this.subscribeOnNewAccessRequests();
    }

    private subscribeOnNewAccessRequests(): void {
        this.userAccessRequestsService.userAccessRequests$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.reloadData());
    }

    public getListData(): Promise<IUserListItemModel[]> {
        return Promise.resolve(this.userAccessRequestsService.userAccessRequests);
    }

    protected acceptSelectedUsersRequests(): void {
        this.userAccessRequestsService.acceptSelectedUsersAccessRequests(this.selectedItems());
    }

    protected confirmRejection(): void {
        this.fiItemsDeleteHelperService
            .requestAccessDeleteConfirm(this.selectedItems())
            .then((usersToDelete: IUserListItemModel[]) => this.rejectSelectedUsersRequests(usersToDelete));
    }

    private rejectSelectedUsersRequests(usersToDelete: IUserListItemModel[]) {
        const selectedItemIds: number[] = usersToDelete.map((user) => user.Id);
        this.userAccessRequestsService.rejectAccessRequest(selectedItemIds).then(() => {
            this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(
                ObjectTypes.User,
                selectedItemIds,
                usersToDelete[0].FullName,
            );
        });
    }

    protected isMobilePhone(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    protected getUserAreas(user: IUserListItemModel): string {
        return user.Areas.map((area: ILightWeightObject) => area.Name).join(', ');
    }

    protected getUserRoles(user: IUserListItemModel): string {
        return user.Roles.map((role: ILightWeightObject) => role.Name).join(', ');
    }

    protected getUserDescription(user: IUserListItemModel): string {
        return `${user.PreferredBusinessUnitName}, ${user.LoginName}`;
    }

    private subscribeOnListDisplayTypeChange(): void {
        if (isDefined(this.currentListDisplayType)) {
            return;
        }

        this.userPreferencesService
            .onListDisplayTypeUpdate$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((value) => {
                if (value === this.itemsListType) this.setCurrentListDisplayType();
            });
    }

    private setCurrentListDisplayType(): void {
        this.currentListDisplayType = this.userPreferencesService.getListDisplayType(this.itemsListType);
        this.cdr.markForCheck();
    }

    protected isDisplayTypeListGrid(): boolean {
        return this.currentListDisplayType === FiDisplayType.Grid;
    }

    protected navigateToUserRequestEditor($event: MouseEvent, userId: number): void {
        $event.preventDefault();
        $event.stopPropagation();
        this.fiStateService.go(FiStateNames.User.EditV2, { Id: userId, isAccessRequesting: true });
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    protected readonly FiProfileSizes = FiProfileSizes;
}
